'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import { createContext, use, useCallback, useEffect, useState } from 'react';

import { cn } from '../../utils/cn';

type ImageLoadingStatus = 'idle' | 'loading' | 'loaded' | 'error';

const AvatarContext = createContext<{
  status: ImageLoadingStatus;
  setStatus: (status: ImageLoadingStatus) => void;
}>({ status: 'idle', setStatus: () => {} });

const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden rounded-full',
  {
    variants: {
      size: {
        sm: 'h-8 w-8',
        default: 'h-10 w-10',
        lg: 'h-12 w-12',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

interface AvatarProps
  extends
    React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof avatarVariants> {
  ref?: React.Ref<HTMLSpanElement>;
}

function Avatar({ className, size, ref, ...props }: AvatarProps) {
  const [status, setStatus] = useState<ImageLoadingStatus>('idle');

  return (
    <AvatarContext value={{ status, setStatus }}>
      <span
        className={cn(avatarVariants({ size }), className)}
        ref={ref}
        {...props}
      />
    </AvatarContext>
  );
}

interface AvatarImageProps extends Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'onLoad' | 'onError'
> {
  onLoadingStatusChange?: (status: ImageLoadingStatus) => void;
}

function AvatarImage({
  src,
  alt,
  className,
  onLoadingStatusChange,
  ...props
}: AvatarImageProps) {
  const { status, setStatus } = use(AvatarContext);

  const updateStatus = useCallback(
    (newStatus: ImageLoadingStatus) => {
      setStatus(newStatus);
      onLoadingStatusChange?.(newStatus);
    },
    [setStatus, onLoadingStatusChange]
  );

  useEffect(() => {
    if (!src) {
      updateStatus('error');
      return;
    }
    updateStatus('loading');
  }, [src, updateStatus]);

  if (!src) return null;

  return (
    <img
      src={src}
      alt={alt}
      className={cn(
        'aspect-square h-full w-full',
        status !== 'loaded' && 'sr-only',
        className
      )}
      onLoad={() => updateStatus('loaded')}
      onError={() => updateStatus('error')}
      {...props}
    />
  );
}

interface AvatarFallbackProps extends React.HTMLAttributes<HTMLSpanElement> {
  delayMs?: number;
}

function AvatarFallback({
  delayMs = 0,
  className,
  children,
  ...props
}: AvatarFallbackProps) {
  const { status } = use(AvatarContext);
  const [canRender, setCanRender] = useState(delayMs === 0);

  useEffect(() => {
    if (delayMs > 0) {
      const timer = setTimeout(() => setCanRender(true), delayMs);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [delayMs]);

  if (status === 'loaded' || !canRender) return null;

  return (
    <span
      className={cn(
        'bg-muted flex h-full w-full items-center justify-center rounded-full',
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export { Avatar, AvatarImage, AvatarFallback, avatarVariants };
export type { AvatarProps, AvatarImageProps, AvatarFallbackProps };
